import { useState, useEffect } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Compass, Flame, Sparkles, X, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { slateCardClass } from "@/lib/card-styles";
import { usePageTourContext } from "@/contexts/PageTourContext";
import { useTranslation } from "react-i18next";

/**
 * First-visit branded welcome modal.
 *
 * Shows once per user (gated by `welcome-seen` localStorage key), before the
 * Home tour auto-starts. Introduces the platform in 3 quick bullet points
 * with two CTAs: "Start tour" (launches Home tour after closing) and
 * "Maybe later" (just closes — tour still auto-starts unless snoozed).
 */

const WELCOME_SEEN_KEY = "welcome-seen";
const GLOBAL_SUPPRESS_KEY = "tour-globally-suppressed";

const BULLET_ICONS = [Sparkles, Flame, Compass] as const;

const BULLET_KEYS = [
  { titleKey: "home.welcome.featOneTitle", title: "Practice speaking", descKey: "home.welcome.featOneDesc", desc: "Pick a topic, generate questions, and practice out loud with instant feedback." },
  { titleKey: "home.welcome.featTwoTitle", title: "Build streaks", descKey: "home.welcome.featTwoDesc", desc: "Practice daily to keep your streak alive, earn XP, and level up." },
  { titleKey: "home.welcome.featThreeTitle", title: "Track progress", descKey: "home.welcome.featThreeDesc", desc: "Save vocabulary, review with spaced repetition, and climb the leaderboard." },
] as const;

export interface WelcomeModalProps {
  /** Called when the user clicks "Start tour" — should trigger the Home tour. */
  onStartTour: () => void;
  /** Called when the user clicks "Maybe later". */
  onDismiss: () => void;
  /** Override the auth gate. Default: only show for authenticated users. */
  forceShow?: boolean;
}

export const WelcomeModal = ({ onStartTour, onDismiss, forceShow }: WelcomeModalProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { setTourActive } = usePageTourContext();

  useEffect(() => {
    if (forceShow) {
      setOpen(true);
      setTourActive(true);
      return;
    }
    try {
      const seen = localStorage.getItem(WELCOME_SEEN_KEY) === "true";
      const suppressed = localStorage.getItem(GLOBAL_SUPPRESS_KEY) === "true";
      if (!seen && !suppressed) {
        setOpen(true);
        setTourActive(true);
      }
    } catch {
      // localStorage unavailable — skip
    }
  }, [forceShow, setTourActive]);

  const handleClose = (open: boolean) => {
    if (!open) {
      try {
        localStorage.setItem(WELCOME_SEEN_KEY, "true");
      } catch {
        // ignore
      }
    }
    setOpen(open);
    if (!open) setTourActive(false);
  };

  const handleStartTour = () => {
    try {
      localStorage.setItem(WELCOME_SEEN_KEY, "true");
    } catch {
      // ignore
    }
    setOpen(false);
    // Don't setTourActive(false) — the tour takes over immediately and
    // sets it to true itself. Setting false here would create a gap that
    // lets the ReviewReminder flash in.
    onStartTour();
  };

  const handleMaybeLater = () => {
    try {
      localStorage.setItem(WELCOME_SEEN_KEY, "true");
    } catch {
      // ignore
    }
    setOpen(false);
    setTourActive(false);
    onDismiss();
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleClose}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[10002] bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            slateCardClass,
            "fixed left-1/2 top-1/2 z-[10003] w-[min(32rem,92vw)] -translate-x-1/2 -translate-y-1/2 space-y-6 p-7 outline-none",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
          )}
        >
          <DialogPrimitive.Close
            className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-stone-200 bg-white text-stone-500 transition-all hover:bg-stone-50 hover:text-stone-700 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-400 dark:hover:bg-stone-700"
            aria-label={t("common.actions.close", "Close")}
          >
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>

          {/* Hero header */}
          <div className="space-y-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-strong text-primary-foreground shadow-md shadow-primary/25">
              <Compass className="h-6 w-6" />
            </div>
            <div>
              {/* Radix names the dialog from `DialogTitle` and describes it from
                  `DialogDescription`; a plain <h2> leaves both missing, which is an
                  accessibility error in the console and an unnamed dialog for a
                  screen reader. `asChild` keeps the existing element and styling. */}
              <DialogPrimitive.Title asChild>
                <h2 className="text-2xl font-bold tracking-tight text-foreground">
                  {t("home.welcome.title", "Welcome to Lyder Talk")}
                </h2>
              </DialogPrimitive.Title>
              <DialogPrimitive.Description asChild>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t("home.welcome.subtitle", "Your daily practice space for speaking a new language with confidence.")}
                </p>
              </DialogPrimitive.Description>
            </div>
          </div>

          {/* Bullet points */}
          <div className="space-y-3">
            {BULLET_KEYS.map((b, i) => {
              const Icon = BULLET_ICONS[i];
              return (
              <div key={b.titleKey} className="flex items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary dark:bg-primary/20">
                  <Icon className="h-4 w-4" />
                </span>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-foreground">{t(b.titleKey, b.title)}</div>
                  <div className="text-xs leading-relaxed text-muted-foreground">
                    {t(b.descKey, b.desc)}
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {/* CTAs */}
          <div className="flex flex-col-reverse gap-2 border-t border-stone-200/60 pt-5 dark:border-stone-700/60 sm:flex-row sm:items-center sm:justify-end sm:space-x-2">
            <button
              type="button"
              onClick={handleMaybeLater}
              className="inline-flex h-10 items-center justify-center rounded-md px-5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              {t("home.welcome.maybeLater", "Maybe later")}
            </button>
            <button
              type="button"
              onClick={handleStartTour}
              className="inline-flex h-10 items-center justify-center rounded-lg bg-primary-strong px-6 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/25 transition-all hover:shadow-lg hover:shadow-primary/30 hover:brightness-110 active:scale-[0.97]"
            >
              {t("home.welcome.startTour", "Start tour")}
              <ChevronRight className="ml-1.5 h-4 w-4" />
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default WelcomeModal;

export { WELCOME_SEEN_KEY, GLOBAL_SUPPRESS_KEY };